import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Header } from '../components/Layout';
import {
  getVendorApiKey, regenerateVendorApiKey, getApiLogs,
  getLeadSources, getDistributionRules, upsertDistributionRule,
  deleteDistributionRule, getUsers, getQuotesAgents,
  getLeadEmail, regenerateLeadEmail,
  getEmailConfig, updateEmailConfig, getEmailTemplates,
  updateEmailTemplate, previewEmailTemplate, testSendEmailTemplate, getEmailLogs,
  getRevenueTargets, updateRevenueTarget, updateTeamRevenueTarget,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Key, RefreshCw, Copy, Shield, BarChart3, Network,
  Trash2, Plus, AlertTriangle, CheckCircle, ExternalLink, Mail,
  Send, Eye, FileEdit, Save, Target, DollarSign, Users, Webhook, FileText, Globe, Zap, UserPlus, Building2, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import CompliancePanel from './CompliancePanel';

// ==================== EMAIL SETTINGS PANEL ====================
const EmailSettingsPanel = () => {
  const [emailConfig, setEmailConfig] = useState({ sender_email: '', sender_name: '', company_name: '', company_address: '', company_phone: '' });
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await getEmailConfig();
      setEmailConfig(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await getEmailTemplates();
      setTemplates(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await getEmailLogs(30);
      setEmailLogs(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchConfig(); fetchTemplates(); fetchLogs(); }, [fetchConfig, fetchTemplates, fetchLogs]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await updateEmailConfig(emailConfig);
      toast.success('Email configuration saved');
    } catch { toast.error('Failed to save config'); }
    finally { setSaving(false); }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await updateEmailTemplate(editingTemplate.id, {
        subject: editingTemplate.subject,
        html: editingTemplate.html,
        name: editingTemplate.name,
        description: editingTemplate.description,
      });
      toast.success('Template saved');
      fetchTemplates();
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const handlePreview = async (tplId) => {
    try {
      const res = await previewEmailTemplate(tplId);
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch { toast.error('Failed to generate preview'); }
  };

  const handleTestSend = async (tplId) => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    try {
      await testSendEmailTemplate(tplId, testEmail);
      toast.success(`Test email sent to ${testEmail}`);
      fetchLogs();
    } catch { toast.error('Failed to send test email'); }
  };

  return (
    <div className="space-y-6">
      {/* Sender Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-5 h-5 text-blue-600" />
          <h2 className="font-heading font-semibold text-slate-900 text-lg">Sender Configuration</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Configure the sender details for all outgoing CRM emails.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-600">Sender Email</Label>
            <Input value={emailConfig.sender_email || ''} onChange={e => setEmailConfig({...emailConfig, sender_email: e.target.value})}
              placeholder="info@breamway.com" className="mt-1" data-testid="sender-email-input" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Sender Name</Label>
            <Input value={emailConfig.sender_name || ''} onChange={e => setEmailConfig({...emailConfig, sender_name: e.target.value})}
              placeholder="Breamway Auto Transport" className="mt-1" data-testid="sender-name-input" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Company Name</Label>
            <Input value={emailConfig.company_name || ''} onChange={e => setEmailConfig({...emailConfig, company_name: e.target.value})}
              placeholder="Breamway Auto Transport" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Company Phone</Label>
            <Input value={emailConfig.company_phone || ''} onChange={e => setEmailConfig({...emailConfig, company_phone: e.target.value})}
              placeholder="+1 (555) 000-0000" className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-600">Company Address</Label>
            <Input value={emailConfig.company_address || ''} onChange={e => setEmailConfig({...emailConfig, company_address: e.target.value})}
              placeholder="277 Osgood Avenue, Houston, TX" className="mt-1" />
          </div>
        </div>
        <Button onClick={handleSaveConfig} disabled={saving} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-email-config-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {/* Email Templates */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <FileEdit className="w-5 h-5 text-emerald-600" />
          <h2 className="font-heading font-semibold text-slate-900 text-lg">Email Templates</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Edit the HTML templates used for customer emails. Use {'{{placeholders}}'} for dynamic data.</p>

        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 mb-4">
          <p className="text-xs font-semibold text-slate-700 mb-2">Available Placeholders:</p>
          <div className="flex flex-wrap gap-1.5">
            {['customer_name','quote_number','vehicle','pickup_address','delivery_address','pickup_date','distance','price_standard','price_expedited','price_enclosed','deposit','carrier_fee','price','company_name','company_address','company_phone','sender_email'].map(p => (
              <code key={p} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-mono">{`{{${p}}}`}</code>
            ))}
          </div>
        </div>

        {templates.map(tpl => (
          <div key={tpl.id} className="border border-slate-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">{tpl.name}</h3>
                <p className="text-xs text-slate-500">{tpl.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePreview(tpl.id)} data-testid={`preview-tpl-${tpl.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" />Preview
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingTemplate({...tpl})} data-testid={`edit-tpl-${tpl.id}`}>
                  <FileEdit className="w-3.5 h-3.5 mr-1" />Edit
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Enter email to send test..." className="flex-1 text-sm" data-testid={`test-email-${tpl.id}`} />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleTestSend(tpl.id)} data-testid={`send-test-${tpl.id}`}>
                <Send className="w-3.5 h-3.5 mr-1" />Send Test
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Email Logs */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-slate-600" />
          <h2 className="font-heading font-semibold text-slate-900 text-lg">Email Delivery Logs</h2>
        </div>
        {emailLogs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No emails sent yet</p>
        ) : (
          <ScrollArea className="h-64">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">Time</th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">To</th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">Subject</th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map((log, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(log.sent_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{log.to}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-[200px] truncate">{log.subject}</td>
                    <td className="px-3 py-2">
                      {log.status === 202 || log.status === 200 ? (
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">Sent</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs font-medium">{log.error || log.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>

      {/* Template Editor Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => { if (!open) setEditingTemplate(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit Email Template: {editingTemplate?.name}</DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-medium text-slate-600">Template Name</Label>
                <Input value={editingTemplate.name} onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Subject Line</Label>
                <Input value={editingTemplate.subject} onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})} className="mt-1"
                  placeholder="Your Vehicle Transport Quote — {{quote_number}}" data-testid="tpl-subject-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">HTML Template Body</Label>
                <textarea value={editingTemplate.html} onChange={e => setEditingTemplate({...editingTemplate, html: e.target.value})}
                  className="w-full h-80 mt-1 p-3 border border-slate-200 rounded-lg font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="tpl-html-textarea" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSaveTemplate} disabled={saving} data-testid="save-template-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>Email Template Preview</DialogTitle>
          </DialogHeader>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <iframe srcDoc={previewHtml} title="Email Preview" className="w-full h-[500px] border-0" sandbox="" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== INVOICE EMAIL TEMPLATES PANEL ====================
const InvoiceEmailTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try { const res = await api.get('/settings/invoice-email-templates'); setTemplates(res.data); } catch {}
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await api.put(`/settings/invoice-email-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        html: editingTemplate.html,
      });
      toast.success('Template saved! All future emails will use this template.');
      fetchTemplates();
      setEditingTemplate(null);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleReset = async (templateId) => {
    try {
      await api.delete(`/settings/invoice-email-templates/${templateId}`);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch { toast.error('Failed to reset'); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-5 h-5 text-blue-600" />
          <h2 className="font-heading font-semibold text-slate-900 text-lg">Invoice & Email Templates</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Customize the branded emails sent to customers. Changes apply to all users immediately.</p>

        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 mb-4">
          <p className="text-xs font-semibold text-blue-800 mb-2">Available Placeholders:</p>
          <div className="flex flex-wrap gap-1.5">
            {['customer_name','invoice_number','vehicle','route','total_price','deposit','cod','payment_method','sign_url'].map(p => (
              <code key={p} className="px-1.5 py-0.5 bg-white text-blue-700 text-xs rounded font-mono border border-blue-200">{`{{${p}}}`}</code>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {templates.map(tpl => (
            <div key={tpl.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-200 transition-colors" data-testid={`invoice-tpl-${tpl.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 text-sm">{tpl.name}</h3>
                    {tpl.signable && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase">Sign Required</span>}
                    {tpl.is_customized && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold">Customized</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Subject: <span className="font-mono">{tpl.subject}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  {tpl.is_customized && (
                    <Button variant="outline" size="sm" onClick={() => handleReset(tpl.id)} data-testid={`reset-tpl-${tpl.id}`}>
                      Reset
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setEditingTemplate({...tpl})} data-testid={`edit-invoice-tpl-${tpl.id}`}>
                    <FileEdit className="w-3.5 h-3.5 mr-1" />Edit
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Template Editor Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => { if (!open) setEditingTemplate(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-template-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit: {editingTemplate?.name}</DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 py-2">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-xs text-amber-800">Changes here will apply to <strong>all users</strong> when they send this email type. Leave HTML blank to use the default branded template.</p>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Subject Line</Label>
                <Input value={editingTemplate.subject} onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})} className="mt-1 font-mono text-sm" data-testid="invoice-tpl-subject" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Custom HTML Body (leave blank for default)</Label>
                <textarea value={editingTemplate.html || ''} onChange={e => setEditingTemplate({...editingTemplate, html: e.target.value})}
                  className="mt-1 w-full h-64 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Paste custom HTML here or leave blank for default branded template..." data-testid="invoice-tpl-html" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-invoice-tpl-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== REVENUE TARGETS PANEL ====================
const RevenueTargetsPanel = () => {
  const [targets, setTargets] = useState([]);
  const [teamTarget, setTeamTarget] = useState(7000);
  const [editingTeam, setEditingTeam] = useState(false);
  const [teamInput, setTeamInput] = useState('7000');
  const [editingUser, setEditingUser] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTargets = useCallback(async () => {
    try {
      const res = await getRevenueTargets();
      setTargets(res.data.users || []);
      setTeamTarget(res.data.team_target || 7000);
      setTeamInput(String(res.data.team_target || 7000));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const handleSaveTeamTarget = async () => {
    setSaving(true);
    try {
      await updateTeamRevenueTarget(parseInt(teamInput) || 7000);
      toast.success('Team target updated');
      setEditingTeam(false);
      fetchTargets();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleSaveUserTarget = async (userId) => {
    setSaving(true);
    try {
      await updateRevenueTarget(userId, parseInt(userInput) || 1500);
      toast.success('Target updated');
      setEditingUser(null);
      fetchTargets();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Team Target */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Team Revenue Target</h3>
              <p className="text-xs text-slate-500">Combined target shown on your Super Admin dashboard</p>
            </div>
          </div>
          {editingTeam ? (
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-sm">$</span>
              <Input value={teamInput} onChange={e => setTeamInput(e.target.value)} type="number" className="w-32 text-sm" data-testid="team-target-input" />
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSaveTeamTarget} disabled={saving} data-testid="save-team-target-btn">
                <Save className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingTeam(false); setTeamInput(String(teamTarget)); }}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-emerald-600" data-testid="team-target-value">${teamTarget.toLocaleString()}</span>
              <Button size="sm" variant="outline" onClick={() => setEditingTeam(true)} data-testid="edit-team-target-btn">
                <FileEdit className="w-3.5 h-3.5 mr-1" />Edit
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Per-User Targets */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Per-User Revenue Targets</h3>
            <p className="text-xs text-slate-500">Set individual monthly targets for each team member</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-targets-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Role</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Monthly Target</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {targets.map(t => (
                <tr key={t.user_id} className="hover:bg-slate-50" data-testid={`target-row-${t.user_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{t.full_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.role === 'superadmin' ? 'bg-rose-50 text-rose-700' :
                      t.role === 'admin' ? 'bg-blue-50 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{t.role}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingUser === t.user_id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-slate-500">$</span>
                        <Input value={userInput} onChange={e => setUserInput(e.target.value)} type="number" className="w-28 text-sm text-right" data-testid={`user-target-input-${t.user_id}`} />
                      </div>
                    ) : (
                      <span className="font-semibold text-slate-900" data-testid={`user-target-value-${t.user_id}`}>${t.target_amount.toLocaleString()}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingUser === t.user_id ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7" onClick={() => handleSaveUserTarget(t.user_id)} disabled={saving} data-testid={`save-user-target-${t.user_id}`}>
                          <Save className="w-3 h-3 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingUser(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7" onClick={() => { setEditingUser(t.user_id); setUserInput(String(t.target_amount)); }} data-testid={`edit-user-target-${t.user_id}`}>
                        <FileEdit className="w-3 h-3 mr-1" />Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {targets.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-slate-400">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== PARTNERS PANEL ====================
const PartnersPanel = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', email: '', full_name: '', company: '', phone: '' });

  const fetchPartners = useCallback(async () => {
    try { const res = await api.get('/partners'); setPartners(res.data); }
    catch { toast.error('Failed to fetch partners'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleCreate = async () => {
    try {
      await api.post('/partners', form);
      toast.success('Partner created');
      setShowCreate(false);
      setForm({ username: '', password: '', email: '', full_name: '', company: '', phone: '' });
      fetchPartners();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create partner'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner and ALL their workspace data?')) return;
    try { await api.delete(`/partners/${id}`); toast.success('Partner deleted'); fetchPartners(); }
    catch { toast.error('Failed to delete partner'); }
  };

  const handleToggle = async (partner) => {
    try {
      await api.put(`/partners/${partner.id}`, { is_active: !partner.is_active });
      toast.success(`Partner ${partner.is_active ? 'suspended' : 'activated'}`);
      fetchPartners();
    } catch { toast.error('Failed to update partner'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />Partner Users
            </h2>
            <p className="text-sm text-slate-500">Manage external partner accounts with isolated workspaces</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-partner-btn">
            <UserPlus className="w-4 h-4 mr-2" />Create Partner
          </Button>
        </div>
        {loading ? <p className="text-sm text-slate-400">Loading...</p> : partners.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No partner users yet. Create one to get started.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead className="bg-slate-50"><tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Partner</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Company</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Users</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Data</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Actions</th>
              </tr></thead>
              <tbody>{partners.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{p.full_name}</div><div className="text-xs text-slate-400">{p.username} | {p.email}</div></td>
                  <td className="px-4 py-3 text-slate-600">{p.company}</td>
                  <td className="px-4 py-3 text-slate-600">{p.sub_user_count || 0} sub-users</td>
                  <td className="px-4 py-3"><span className="text-xs text-slate-500">{p.quote_count || 0} quotes, {p.order_count || 0} orders</span></td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {p.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(p)} data-testid={`toggle-partner-${p.id}`}>
                      {p.is_active ? 'Suspend' : 'Activate'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(p.id)} data-testid={`delete-partner-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-partner-dialog">
          <DialogHeader><DialogTitle>Create Partner User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Username</Label><Input value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} data-testid="partner-username" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} data-testid="partner-password" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="partner-email" /></div>
            <div><Label>Full Name</Label><Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} data-testid="partner-fullname" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} data-testid="partner-company" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="partner-phone" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} data-testid="submit-partner-btn">Create Partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== WEBHOOKS PANEL ====================
const WebhooksPanel = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ url: '', events: ['lead.created', 'lead.status_changed'], secret: '' });
  const [testing, setTesting] = useState(null);

  const validEvents = ['lead.created', 'lead.status_changed', 'lead.quoted', 'lead.do_not_call_requested'];

  const fetchWebhooks = useCallback(async () => {
    try { const res = await api.get('/settings/webhooks'); setWebhooks(res.data); }
    catch { toast.error('Failed to fetch webhooks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleCreate = async () => {
    try {
      await api.post('/settings/webhooks', form);
      toast.success('Webhook created');
      setShowCreate(false);
      setForm({ url: '', events: ['lead.created', 'lead.status_changed'], secret: '' });
      fetchWebhooks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create webhook'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/settings/webhooks/${id}`); toast.success('Webhook deleted'); fetchWebhooks(); }
    catch { toast.error('Failed to delete webhook'); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await api.post(`/settings/webhooks/${id}/test`);
      if (res.data.success) toast.success(`Test successful (HTTP ${res.data.status_code})`);
      else toast.error(`Test failed (HTTP ${res.data.status_code}): ${res.data.response}`);
    } catch { toast.error('Test failed'); }
    finally { setTesting(null); }
  };

  const toggleEvent = (event) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(event) ? f.events.filter(e => e !== event) : [...f.events, event]
    }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />Outbound Webhooks
            </h2>
            <p className="text-sm text-slate-500">Configure webhook URLs to receive lead events</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-webhook-btn">
            <Plus className="w-4 h-4 mr-2" />Add Webhook
          </Button>
        </div>
        {loading ? <p className="text-sm text-slate-400">Loading...</p> : webhooks.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No webhooks configured yet.</p>
        ) : (
          <div className="space-y-3">
            {webhooks.map(wh => (
              <div key={wh.id} className="border border-slate-200 rounded-lg p-4" data-testid={`webhook-${wh.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <span className="font-mono text-sm font-medium text-slate-800">{wh.url}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${wh.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {wh.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      {wh.events?.map(e => (
                        <span key={e} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">{e}</span>
                      ))}
                    </div>
                    {wh.last_triggered_at && <p className="text-xs text-slate-400 mt-1">Last triggered: {new Date(wh.last_triggered_at).toLocaleString()}</p>}
                    {wh.failure_count > 0 && <p className="text-xs text-red-500">Failures: {wh.failure_count}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => handleTest(wh.id)} disabled={testing === wh.id} data-testid={`test-webhook-${wh.id}`}>
                      {testing === wh.id ? 'Testing...' : 'Test'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(wh.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-webhook-dialog">
          <DialogHeader><DialogTitle>Add Webhook</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Webhook URL</Label><Input value={form.url} onChange={e => setForm(f => ({...f, url: e.target.value}))} placeholder="https://your-server.com/webhook" data-testid="webhook-url" /></div>
            <div><Label>Secret (optional)</Label><Input value={form.secret} onChange={e => setForm(f => ({...f, secret: e.target.value}))} placeholder="HMAC secret for signature verification" /></div>
            <div>
              <Label className="mb-2 block">Events</Label>
              <div className="space-y-1">
                {validEvents.map(ev => (
                  <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} className="rounded" />
                    {ev}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} data-testid="submit-webhook-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== API DOCS PANEL ====================
const ApiDocsPanel = ({ apiKey }) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const endpoints = [
    { method: 'GET', path: '/api/v1/leads', desc: 'List leads with filters', params: 'status, do_not_call, call_attempts_lt, updated_before, updated_after, limit, offset' },
    { method: 'GET', path: '/api/v1/leads/{id}', desc: 'Get single lead by ID' },
    { method: 'PATCH', path: '/api/v1/leads/{id}', desc: 'Update lead fields', params: 'customer_name, phone, email, status, do_not_call, tags, notes, etc.' },
    { method: 'POST', path: '/api/v1/leads/{id}/log-call', desc: 'Log a call attempt', params: 'call_status, duration_seconds, recording_url, transcript, summary, outcome_category' },
    { method: 'POST', path: '/api/v1/leads/{id}/log-sms', desc: 'Log an SMS', params: 'direction, message, status' },
    { method: 'POST', path: '/api/v1/leads/bulk-dnc', desc: 'Bulk mark leads as Do Not Call', params: 'phone_numbers (array)' },
    { method: 'GET', path: '/api/v1/leads/{id}/call-logs', desc: 'Get call history for a lead' },
    { method: 'GET', path: '/api/v1/leads/{id}/sms-logs', desc: 'Get SMS history for a lead' },
  ];
  const methodColors = { GET: 'bg-emerald-100 text-emerald-700', POST: 'bg-blue-100 text-blue-700', PATCH: 'bg-amber-100 text-amber-700' };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h2 className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-slate-600" />External API Documentation
        </h2>
        <p className="text-sm text-slate-500 mb-4">Use these endpoints for external integrations (AI calling, automation, etc.)</p>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Authentication</span>
          </div>
          <p className="text-xs text-slate-500 mb-2">Include your API key in the request header:</p>
          <code className="text-xs bg-slate-800 text-emerald-400 px-3 py-1.5 rounded block font-mono">X-API-Key: {apiKey || 'your-api-key-here'}</code>
          <p className="text-xs text-slate-400 mt-2">Base URL: <span className="font-mono text-slate-600">{baseUrl}</span></p>
        </div>

        <div className="space-y-2">
          {endpoints.map((ep, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`api-endpoint-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${methodColors[ep.method]}`}>{ep.method}</span>
                <code className="text-sm font-mono text-slate-800">{ep.path}</code>
              </div>
              <p className="text-xs text-slate-500">{ep.desc}</p>
              {ep.params && <p className="text-xs text-slate-400 mt-1">Params: <span className="font-mono">{ep.params}</span></p>}
            </div>
          ))}
        </div>

        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-700 font-medium">Rate Limits</p>
          <p className="text-xs text-amber-600">100 requests/minute per API key. Contact admin to increase.</p>
        </div>

        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-700 font-medium">Example: Log a Call</p>
          <pre className="text-xs text-blue-800 font-mono mt-1 whitespace-pre-wrap">{`curl -X POST "${baseUrl}/api/v1/leads/{lead_id}/log-call" \\
  -H "X-API-Key: ${apiKey || 'your-key'}" \\
  -H "Content-Type: application/json" \\
  -d '{"call_status":"completed","duration_seconds":120,"summary":"Customer interested","outcome_category":"interested"}'`}</pre>
        </div>
      </div>
    </div>
  );
};



const AdminPanel = () => {
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsTotal, setApiLogsTotal] = useState(0);
  const [leadSources, setLeadSources] = useState([]);
  const [distRules, setDistRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [addRuleDialog, setAddRuleDialog] = useState(false);
  const [newRule, setNewRule] = useState({ agent_name: '', source: 'default', weight: 1 });
  const [leadEmail, setLeadEmail] = useState('');
  const [regenEmailConfirm, setRegenEmailConfirm] = useState(false);

  const fetchApiKey = useCallback(async () => {
    try {
      const res = await getVendorApiKey();
      setApiKey(res.data.api_key);
    } catch { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await getApiLogs(50);
      setApiLogs(res.data.logs);
      setApiLogsTotal(res.data.total);
    } catch { /* ignore */ }
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      const res = await getLeadSources();
      setLeadSources(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchRules = useCallback(async () => {
    try {
      const res = await getDistributionRules();
      setDistRules(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await getQuotesAgents();
      setAgents(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchLeadEmail = useCallback(async () => {
    try {
      const res = await getLeadEmail();
      setLeadEmail(res.data.email);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchApiKey();
    fetchLogs();
    fetchSources();
    fetchRules();
    fetchAgents();
    fetchLeadEmail();
  }, [fetchApiKey, fetchLogs, fetchSources, fetchRules, fetchAgents, fetchLeadEmail]);

  const handleRegenerate = async () => {
    try {
      const res = await regenerateVendorApiKey();
      setApiKey(res.data.api_key);
      setConfirmRegen(false);
      toast.success('API key regenerated! Update all vendor integrations with the new key.');
    } catch {
      toast.error('Failed to regenerate API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleAddRule = async () => {
    if (!newRule.agent_name) {
      toast.error('Please select an agent');
      return;
    }
    try {
      await upsertDistributionRule(newRule.agent_name, newRule.source, newRule.weight, true);
      toast.success('Distribution rule saved');
      setAddRuleDialog(false);
      setNewRule({ agent_name: '', source: 'default', weight: 1 });
      fetchRules();
    } catch {
      toast.error('Failed to save rule');
    }
  };

  const handleDeleteRule = async (agentName, source) => {
    try {
      await deleteDistributionRule(agentName, source);
      toast.success('Rule deleted');
      fetchRules();
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const handleRegenEmail = async () => {
    try {
      const res = await regenerateLeadEmail();
      setLeadEmail(res.data.email);
      setRegenEmailConfirm(false);
      toast.success('Lead delivery email regenerated');
    } catch {
      toast.error('Failed to regenerate email');
    }
  };

  const vendorDocsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/vendor/docs`;
  const apiEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/leads/incoming`;
  const emailEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/leads/email-incoming`;
  const emailWebhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/leads/email-webhook`;

  const handleToggleRule = async (rule) => {
    try {
      await upsertDistributionRule(rule.agent_name, rule.source, rule.weight, !rule.enabled);
      toast.success(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`);
      fetchRules();
    } catch {
      toast.error('Failed to update rule');
    }
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <Layout>
      <Header title="Admin Control Panel" />
      <div className="p-6" data-testid="admin-panel-page">
        <Tabs defaultValue="api-keys" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg" data-testid="admin-tabs">
            <TabsTrigger value="api-keys" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-api-keys">
              <Key className="w-4 h-4 mr-2" />API Keys
            </TabsTrigger>
            <TabsTrigger value="distribution" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-distribution">
              <Network className="w-4 h-4 mr-2" />Lead Distribution
            </TabsTrigger>
            <TabsTrigger value="sources" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-sources">
              <BarChart3 className="w-4 h-4 mr-2" />Lead Sources
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-logs">
              <Shield className="w-4 h-4 mr-2" />API Logs
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-email">
              <Mail className="w-4 h-4 mr-2" />Email Settings
            </TabsTrigger>
            <TabsTrigger value="invoice-emails" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-invoice-emails">
              <Send className="w-4 h-4 mr-2" />Invoice Emails
            </TabsTrigger>
            <TabsTrigger value="revenue-targets" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-revenue-targets">
              <Target className="w-4 h-4 mr-2" />Revenue Targets
            </TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-partners">
              <Users className="w-4 h-4 mr-2" />Partners
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-webhooks">
              <Webhook className="w-4 h-4 mr-2" />Webhooks
            </TabsTrigger>
            <TabsTrigger value="api-docs" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-api-docs">
              <FileText className="w-4 h-4 mr-2" />API Docs
            </TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700" data-testid="tab-compliance">
              <ShieldAlert className="w-4 h-4 mr-2" />Compliance
            </TabsTrigger>
          </TabsList>

          {/* API Keys Tab */}
          <TabsContent value="api-keys" className="space-y-6" data-testid="api-keys-content">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-slate-900 text-lg mb-1">Vendor API Key</h2>
              <p className="text-sm text-slate-500 mb-6">This key is used by external lead vendors to submit leads via the API.</p>

              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 relative">
                  <Input
                    readOnly
                    value={showKey ? apiKey : '•'.repeat(Math.min(apiKey.length, 40))}
                    className="font-mono text-sm pr-20"
                    data-testid="api-key-display"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowKey(!showKey)} data-testid="toggle-key-visibility">
                      {showKey ? 'Hide' : 'Show'}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copyToClipboard(apiKey)} data-testid="copy-api-key">
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmRegen(true)} data-testid="regenerate-key-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />Regenerate
                </Button>
              </div>

              {/* API Endpoint Info */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                <h3 className="font-semibold text-slate-900 text-sm mb-3">API Endpoint</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-mono font-bold rounded">POST</span>
                    <code className="text-sm text-slate-700 font-mono">{baseUrl}/api/leads/incoming</code>
                    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(`${baseUrl}/api/leads/incoming`)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p className="mb-1"><strong>Header:</strong> <code className="bg-slate-200 px-1 rounded">X-API-Key: {showKey ? apiKey : '••••••••'}</code></p>
                    <p><strong>Content-Type:</strong> <code className="bg-slate-200 px-1 rounded">application/json</code></p>
                  </div>
                  <a href={`${baseUrl}/api/leads/specs`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    <ExternalLink className="w-3.5 h-3.5" />View Full API Documentation
                  </a>
                </div>
              </div>
            </div>

            {/* Sample Request */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Sample cURL Request</h3>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-emerald-400 font-mono whitespace-pre-wrap">
{`curl -X POST "${baseUrl}/api/leads/incoming" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -d '{
    "name": "John Doe",
    "phone": "1234567890",
    "email": "john@example.com",
    "vehicle_year": "2020",
    "vehicle_make": "Toyota",
    "vehicle_model": "Camry",
    "pickup_city": "Los Angeles",
    "pickup_state": "CA",
    "delivery_city": "Houston",
    "delivery_state": "TX",
    "source": "Lead Vendor 1"
  }'`}
                </pre>
              </div>
            </div>

            {/* Lead Delivery Email */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-5 h-5 text-blue-600" />
                <h2 className="font-heading font-semibold text-slate-900 text-lg">Lead Delivery Email</h2>
              </div>
              <p className="text-sm text-slate-500 mb-4">Vendors can send leads to this email address in plain-text format. Leads will auto-create in your CRM.</p>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 relative">
                  <Input readOnly value={leadEmail || 'Loading...'} className="font-mono text-sm text-blue-700 bg-blue-50 border-blue-200" data-testid="lead-delivery-email" />
                  <Button variant="ghost" size="sm" className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2" onClick={() => copyToClipboard(leadEmail)} data-testid="copy-lead-email">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => setRegenEmailConfirm(true)} data-testid="regen-email-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />Regenerate
                </Button>
              </div>

              {/* Email Format Template */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-semibold text-slate-900 text-xs uppercase tracking-wider mb-3">Required Email Format (Plain Text)</h3>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-emerald-400 font-mono whitespace-pre-wrap">{`Name: John Smith
Pickup City: Redding
Pickup State: CA
Pickup Zip: 96003
Delivery City: Orlando
Delivery State: FL
Delivery Zip: 32801
Year: 2004
Make: Ford
Model: Focus
Pickup Date: 04/30/2014
Running: true
Email: testing@emailaddress.com
Phone: 111-111-1111
Phone 2: 111-111-1111
Notes: Notes go here.
Lead Source ID#: BR000000`}</pre>
                </div>
                <p className="text-xs text-slate-500 mt-2">Leads sent as plain text (not HTML) to the email above will be auto-parsed and added to the CRM.</p>
              </div>

              {/* Email API Endpoint */}
              <div className="mt-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-semibold text-slate-900 text-xs uppercase tracking-wider mb-2">Email Lead API Endpoint</h3>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-mono font-bold rounded">POST</span>
                  <code className="text-sm text-slate-700 font-mono">{emailEndpoint}</code>
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(emailEndpoint)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Send the plain-text body as JSON: <code className="bg-slate-200 px-1 rounded">{"{"}"body": "Name: ...\nPickup City: ..."{"}"}</code></p>
              </div>

              {/* SendGrid Inbound Parse Webhook */}
              <div className="mt-4 bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <h3 className="font-semibold text-emerald-900 text-xs uppercase tracking-wider mb-2">SendGrid Inbound Parse Webhook URL</h3>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-mono font-bold rounded">POST</span>
                  <code className="text-sm text-emerald-700 font-mono break-all">{emailWebhookUrl}</code>
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(emailWebhookUrl)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-emerald-700 mt-2 font-medium">Setup Steps:</p>
                <ol className="text-xs text-slate-600 mt-1 space-y-1 list-decimal list-inside">
                  <li>Go to SendGrid Dashboard &rarr; Settings &rarr; Inbound Parse</li>
                  <li>Add this webhook URL for your domain (e.g., leads.breamway.com)</li>
                  <li>Set MX record for your domain to point to <code className="bg-slate-200 px-1 rounded">mx.sendgrid.net</code></li>
                  <li>Emails sent to any address @leads.breamway.com will auto-create leads</li>
                </ol>
              </div>
            </div>

            {/* Vendor Docs Link */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-blue-800 text-sm">Vendor API Documentation</h3>
                <p className="text-xs text-blue-600">Share this link with your vendors for full API docs</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(vendorDocsUrl)} className="text-blue-700" data-testid="copy-vendor-docs">
                  <Copy className="w-3.5 h-3.5 mr-1" />Copy Link
                </Button>
                <a href={vendorDocsUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="text-blue-700 border-blue-300" data-testid="open-vendor-docs">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />Open Docs
                  </Button>
                </a>
              </div>
            </div>

            {/* Phone Migration Tool */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-amber-800 text-sm">Phone Number Migration</h3>
                <p className="text-xs text-amber-600">Format ALL existing phone numbers to E.164 format (+1XXXXXXXXXX). This is a one-time migration.</p>
              </div>
              <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-100" data-testid="migrate-phones-btn"
                onClick={async () => { try { const res = await api.post('/admin/migrate-phones'); toast.success(`Migration complete: ${res.data.stats.quotes_updated} quotes, ${res.data.stats.orders_updated} orders updated. ${res.data.stats.invalid_phones} invalid phones found.`); } catch { toast.error('Migration failed'); } }}>
                Run Migration
              </Button>
            </div>

            {/* Regenerate Email Confirm */}
            <Dialog open={regenEmailConfirm} onOpenChange={setRegenEmailConfirm}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-amber-600">Regenerate Email Address?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-600 py-2">This will generate a new lead delivery email. The old address will stop working. Update all vendor configurations.</p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRegenEmailConfirm(false)}>Cancel</Button>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleRegenEmail}>Regenerate</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Lead Distribution Tab */}
          <TabsContent value="distribution" className="space-y-6" data-testid="distribution-content">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading font-semibold text-slate-900 text-lg mb-1">Lead Distribution Rules</h2>
                  <p className="text-sm text-slate-500">Configure how incoming vendor leads are automatically assigned to agents.</p>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setAddRuleDialog(true)} data-testid="add-rule-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Rule
                </Button>
              </div>

              {distRules.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Network className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm mb-1">No distribution rules configured</p>
                  <p className="text-slate-400 text-xs">Leads will not be auto-assigned until rules are created.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="distribution-rules-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Agent</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Weight</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {distRules.map((rule, idx) => (
                        <tr key={idx} className="hover:bg-slate-50" data-testid={`dist-rule-${idx}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{rule.agent_name}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-mono">{rule.source}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{rule.weight}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleRule(rule)} className="cursor-pointer" data-testid={`toggle-rule-${idx}`}>
                              {rule.enabled ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" />Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs font-medium">
                                  Disabled
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                              onClick={() => handleDeleteRule(rule.agent_name, rule.source)}
                              data-testid={`delete-rule-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Lead Sources Tab */}
          <TabsContent value="sources" className="space-y-6" data-testid="sources-content">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-slate-900 text-lg mb-1">Lead Sources</h2>
              <p className="text-sm text-slate-500 mb-6">Overview of all lead sources and their volumes.</p>

              {leadSources.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No lead sources tracked yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {leadSources.map((src) => (
                    <div key={src.name} className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid={`source-${src.name}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900 text-sm">{src.name}</span>
                        <span className="text-2xl font-heading font-bold text-blue-600">{src.count.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5">
                        <div className="bg-blue-600 h-1.5 rounded-full" style={{
                          width: `${Math.min(100, (src.count / Math.max(...leadSources.map(s => s.count))) * 100)}%`
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* API Logs Tab */}
          <TabsContent value="logs" className="space-y-6" data-testid="logs-content">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading font-semibold text-slate-900 text-lg mb-1">API Activity Logs</h2>
                  <p className="text-sm text-slate-500">Total: {apiLogsTotal.toLocaleString()} API calls</p>
                </div>
                <Button variant="outline" onClick={fetchLogs} data-testid="refresh-logs">
                  <RefreshCw className="w-4 h-4 mr-2" />Refresh
                </Button>
              </div>

              {apiLogs.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No API activity recorded yet.</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <table className="w-full text-sm" data-testid="api-logs-table">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Timestamp</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quote #</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {apiLogs.map((log, idx) => (
                        <tr key={idx} className="hover:bg-slate-50" data-testid={`log-row-${idx}`}>
                          <td className="px-4 py-2.5 text-xs text-slate-600 font-mono">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{log.type}</span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-700">{log.source || '-'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-blue-600">{log.quote_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </div>
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="email" className="space-y-6" data-testid="email-settings-content">
            <EmailSettingsPanel />
          </TabsContent>

          {/* Invoice Email Templates Tab */}
          <TabsContent value="invoice-emails" className="space-y-6" data-testid="invoice-emails-content">
            <InvoiceEmailTemplatesPanel />
          </TabsContent>

          {/* Revenue Targets Tab */}
          <TabsContent value="revenue-targets" className="space-y-6" data-testid="revenue-targets-content">
            <RevenueTargetsPanel />
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-6" data-testid="partners-content">
            <PartnersPanel />
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-6" data-testid="webhooks-content">
            <WebhooksPanel />
          </TabsContent>

          {/* API Docs Tab */}
          <TabsContent value="api-docs" className="space-y-6" data-testid="api-docs-content">
            <ApiDocsPanel apiKey={apiKey} />
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-6" data-testid="compliance-content">
            <CompliancePanel />
          </TabsContent>

        </Tabs>
      </div>

      {/* Regenerate API Key Confirmation */}
      <Dialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <DialogContent className="max-w-md" data-testid="regen-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />Regenerate API Key?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will invalidate the current API key immediately. All vendors using the old key will stop receiving leads until they update their configuration.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRegen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRegenerate} data-testid="confirm-regen-btn">
              Yes, Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Distribution Rule */}
      <Dialog open={addRuleDialog} onOpenChange={setAddRuleDialog}>
        <DialogContent className="max-w-md" data-testid="add-rule-dialog">
          <DialogHeader>
            <DialogTitle>Add Distribution Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="form-label">Agent Name</Label>
              <select
                className="form-input"
                value={newRule.agent_name}
                onChange={e => setNewRule({ ...newRule, agent_name: e.target.value })}
                data-testid="rule-agent-select"
              >
                <option value="">Select an agent...</option>
                {agents.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              {agents.length === 0 && (
                <div className="mt-2">
                  <Input
                    placeholder="Type agent name manually"
                    value={newRule.agent_name}
                    onChange={e => setNewRule({ ...newRule, agent_name: e.target.value })}
                    data-testid="rule-agent-input"
                  />
                </div>
              )}
            </div>
            <div>
              <Label className="form-label">Lead Source</Label>
              <select
                className="form-input"
                value={newRule.source}
                onChange={e => setNewRule({ ...newRule, source: e.target.value })}
                data-testid="rule-source-select"
              >
                <option value="default">Default (All Sources)</option>
                {leadSources.map(s => (
                  <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
                ))}
                <option value="Lead Vendor 1">Lead Vendor 1</option>
                <option value="vendor">Vendor</option>
              </select>
            </div>
            <div>
              <Label className="form-label">Weight (Higher = More Leads)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={newRule.weight}
                onChange={e => setNewRule({ ...newRule, weight: parseInt(e.target.value) || 1 })}
                data-testid="rule-weight-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddRuleDialog(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAddRule} data-testid="save-rule-btn">
              Save Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminPanel;
